import React from 'react';
import { faro, LogLevel } from '@grafana/faro-react';
import { colors } from '@skyslope/mache';
import type { Nullable } from '@skyslope/utils';
import format from 'date-fns/format';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { type ITemplateRecipient } from '@/store/templateCreate/template-types';
import type { IRecipient } from '../common/recipients/types';
import type { IProperty } from '../dtm/types';
import type { IEnvelopeMetadata } from '../store/dtm/envelopes/types';
import type { IUser } from '../store/dtm/recipients/types';
import type { IRecipientBody, IDocumentBody, IEnvelopeHistoryBody } from '../store/envelopeHistory/types';
import {
  type ISigner,
  type IGroups,
  type IGroup,
  type ICopyCache,
  type IBlocks,
  type ISelectedBlocks,
  type IBlock,
  StrikeType,
} from '../store/senderBuild/types';
import type { IShareRecord, ITemplateUser, IUserOffice, SubGroup } from '../store/templateSharing/types';
import { getEnvelopeCounts } from './api/envelope/envelope-api';
import type { OfficeUser } from './api/prime/prime';
import { BLOCK_TYPES, BLOCK_TYPE_KEYS, SIGNER_COLORS } from './constants';
import { createRandomId } from './randomId';

dayjs.extend(advancedFormat);

interface IPasteMultipleBlocks {
  copyCache: ICopyCache;
  blocks: IBlocks;
  pasteCounter: number;
  pageHeight: number;
  pageWidth: number;
  selectedBlocks: ISelectedBlocks;
  currentPage: number;
  currentDocument: string;
  blockGroups: IGroups;
  envelopeId: string;
}

export const allOfficeGuid = '00000000-0000-0000-0000-000000000000';
// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const hex2rgba = (hex: string, opacity = 0.2) => {
  if (hex) {
    const [r, g, b] = hex.match(/\w\w/g)!.map((x) => parseInt(x, 16));
    return `rgba(${r},${g},${b},${opacity})`;
  }
  return '';
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getValueBasedOnBlockType = (
  blockType: string,
  signer: ISigner,
  dateFormat: string,
  currentValue?: string | boolean
) => {
  let value: string | boolean = currentValue || '';
  switch (blockType) {
    case BLOCK_TYPE_KEYS.SIGNATURE:
      value = 'Signature';
      break;
    case BLOCK_TYPE_KEYS.FULL_NAME:
      value = generateFullName(signer);
      break;
    case BLOCK_TYPE_KEYS.INITIALS:
      value = generateInitials(signer);
      break;
    case BLOCK_TYPE_KEYS.DATE:
      value = dateFormat;
      break;
    case BLOCK_TYPE_KEYS.TIME:
      value = `${dateFormat}, HH:MM:SS`;
      break;
    case BLOCK_TYPE_KEYS.CHECKBOX:
      value = currentValue || false;
      break;
    default:
      break;
  }
  return value;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const generateInitials = (signer: ISigner) => {
  if (signer) {
    return signer!.firstName[0] + (signer!.lastName ? `${signer!.lastName[0]}` : '');
  }
  return '';
};
// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const generateFullName = (signer?: ISigner) => {
  if (signer) {
    return signer!.firstName + (signer!.lastName ? ` ${signer!.lastName}` : '');
  }
  return '';
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const generateEmailSubject = () => 'You have documents to sign';

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const generateEmailBody = () => 'Your documents are ready to review and sign.';

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getGroupById = (currentGroupId: string, blockGroups: IGroups) => {
  let foundGroup: IGroup | null = null;
  Object.keys(blockGroups).forEach((documentId) => {
    Object.keys(blockGroups[documentId]).forEach((pageNumber) => {
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      Object.keys(blockGroups[documentId][pageNumber]).find((groupId) => {
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        const group = blockGroups[documentId][pageNumber][groupId] as IGroup;
        if (group.id === currentGroupId) {
          foundGroup = group;
        }
      });
    });
  });
  return foundGroup;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const buildSelectBoxPosition = (blocks: IBlock[]) => {
  let topLeftX = Infinity;
  let topLeftY = Infinity;
  let bottomRightX = 0;
  let bottomRightY = 0;
  blocks.forEach((block) => {
    if (block.x! < topLeftX) {
      topLeftX = block.x!;
    }
    if (block.y! < topLeftY) {
      topLeftY = block.y!;
    }
    if (block.x! + block.width! > bottomRightX) {
      bottomRightX = block.x! + block.width!;
    }
    if (block.y! + block.height! > bottomRightY) {
      bottomRightY = block.y! + block.height!;
    }
  });
  return {
    x: topLeftX,
    y: topLeftY,
    width: bottomRightX - topLeftX,
    height: bottomRightY - topLeftY,
  };
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const createCopiedCheckboxGroup = (
  block: IBlock,
  newGroupId: string,
  newBlockId: string,
  blockGroups: IGroups,
  envelopeId: string,
  currentPage: number,
  currentDocument: string
) => {
  const currentGroup = getGroupById(block.groupId!, blockGroups) as unknown as IGroup;
  const currentValidationRule = currentGroup!.validation.rule;
  const currentValidtionValue = currentGroup!.validation.value;

  const group: IGroup = {
    envelopeId,
    pageNumber: currentPage,
    blockGroupType: 'CheckboxGroup',
    id: newGroupId,
    blockIds: [newBlockId],
    documentId: currentDocument,
    validation: {
      rule: currentValidationRule,
      value: currentValidtionValue,
    },
  };
  return group;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const pasteCacheBlocks = (
  args: IPasteMultipleBlocks
): {
  blocksWithNewIds: IBlock[];
  newGroups?: { [groupId: string]: IGroup };
  updatedGroup?: { currentGroup: IGroup; newBlock: IBlock[] };
} => {
  const {
    copyCache,
    pageHeight,
    pageWidth,
    selectedBlocks,
    blocks,
    currentPage,
    currentDocument,
    blockGroups,
    envelopeId,
  } = args;
  const { pasteCounter } = copyCache;
  const firstBlock = copyCache.blocks[0];
  const selectBox =
    selectedBlocks.blockIndices.length > 1
      ? buildSelectBoxPosition(copyCache.blocks)
      : { x: null, y: null, height: null, width: null };
  const x = selectBox.x || firstBlock.x!;
  const y = selectBox.y || firstBlock.y!;
  const height = selectBox.height || firstBlock.height!;
  const width = selectBox.width || firstBlock.width!;
  const blocksWithNewIds: IBlock[] = [];
  const xValue = x + (16 * copyCache.pasteCounter + width);
  const yValue = y + (16 * copyCache.pasteCounter + height);
  const seenGroupIds = {};
  const newGroups = {};
  const newBlock: IBlock[] = [];
  const updatedGroup = { currentGroup: {}, newBlock };
  let group;
  let xBounds = false;
  let yBounds = false;
  let currentGroupId: string | null;

  copyCache.blocks.forEach((block, index) => {
    const lastCopiedBlockIndex = blocks[currentDocument][currentPage].length - 1;
    const currentBlockIndex = selectedBlocks.blockIndices[index];
    const currentBlock =
      block ||
      blocks[selectedBlocks.documentId!][selectedBlocks.pageIndex!][currentBlockIndex] ||
      blocks[currentDocument][currentPage][lastCopiedBlockIndex];
    const newBlockId = createRandomId();
    let newGroupId = null;
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    if (currentBlock.groupId && !seenGroupIds[currentBlock.groupId]) {
      newGroupId = createRandomId();
      currentGroupId = newGroupId;
      const newGroup = createCopiedCheckboxGroup(
        currentBlock,
        newGroupId,
        newBlockId,
        blockGroups,
        envelopeId,
        currentPage,
        currentDocument
      );
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      newGroups[newGroupId] = newGroup;
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      seenGroupIds[currentBlock.groupId] = newGroupId;
    } else if (!currentBlock.groupId) {
      currentGroupId = null;
      // @ts-expect-error -- This code was written prior to enforcing lint rules
    } else if (currentBlock.groupId && seenGroupIds[currentBlock.groupId]) {
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      newGroupId = seenGroupIds[currentBlock.groupId];
      currentGroupId = newGroupId;
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      newGroups[newGroupId].blockIds.push(newBlockId);
    } else if (currentBlock.groupId && !(seenGroupIds as any)[currentBlock.groupId]) {
      // This condition is identical to the first if condition so this block will never execute
      currentGroupId = currentBlock.groupId;
      group = blockGroups[currentDocument][currentPage][currentGroupId] as IGroup;
      updatedGroup.currentGroup = group;
      updatedGroup.newBlock = blocksWithNewIds;
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      seenGroupIds[currentBlock.groupId] = currentGroupId;
    }
    if (xValue >= pageWidth && yValue >= pageHeight) {
      xBounds = true;
      yBounds = true;
    } else if (xValue >= pageWidth) {
      xBounds = true;
    } else if (yValue >= pageHeight) {
      yBounds = true;
    }

    blocksWithNewIds.push({
      ...block,
      blockId: newBlockId,
      ...(newGroupId && { groupId: currentGroupId! }),
      x: xBounds ? currentBlock.x! : block.x! + 16 * pasteCounter,
      y: yBounds ? currentBlock.y! : block.y! + 16 * pasteCounter,
      pageNumber: currentPage,
    });

    xBounds = false;
    yBounds = false;
  });
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  return { blocksWithNewIds, newGroups, updatedGroup };
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const concatStrings = (firstName?: string, middleName?: string, lastName?: string) => {
  return [firstName, middleName, lastName].filter(Boolean).join(' ');
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const concatObject = (recipient?: IRecipientBody) => {
  return [recipient?.firstName, recipient?.middleName, recipient?.lastName].filter(Boolean).join(' ');
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getTimezoneCode = () => {
  const match = new Date().toString().match(/\((.+)\)/);
  if (match!.length < 2) {
    return '';
  }
  const longTimezone = match![1];
  return longTimezone
    .split('')
    .filter((c) => /[A-Z]/.test(c))
    .join('');
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const generateFileName = (documents: IDocumentBody[]) => {
  if (!documents) {
    return '';
  }
  return documents.length > 1 ? `digisign-documents-${format(new Date(), 'MM-dd-yyyy')}.pdf` : documents[0].fileName;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const saveFile = (url: string, fileName: string) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.append(link);
  link.click();
  link.remove();
  window.addEventListener('focus', () => URL.revokeObjectURL(url), { once: true });
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const printFile = (url: string) => {
  const iframe = document.createElement('iframe');
  iframe.src = url;
  iframe.style.display = 'none';
  document.body.append(iframe);
  iframe.contentWindow?.print();
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getRecipientGroup = (recipient: IRecipientBody, envelope: IEnvelopeHistoryBody) => {
  if (recipient.role === 'Signer') {
    const signerDetail = envelope.signerDetails!.details!.find((item) => item.value?.signerId === recipient.id);
    const group = envelope.signingGroups!.find((group) => group.id === signerDetail!.value!.signingGroupId!);
    return group;
  }
  return null;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const bold = (s?: string) => {
  return <strong>{s}</strong>;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const boldAll = (recipients: IRecipientBody[]) => {
  return recipients?.map((r, i) => {
    if (i === recipients.length - 1 && recipients.length > 1) {
      return bold(`& ${r.firstName}`);
    }
    if (recipients.length === 1) {
      return bold(`${r.firstName}`);
    }
    return bold(`${r.firstName}, `);
  });
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const setEndOfContenteditable = (contentEditableElement: any) => {
  let range;
  let selection;
  if (document.createRange) {
    range = document.createRange(); // Create a range (a range is a like the selection but invisible)
    range.selectNodeContents(contentEditableElement); // Select the entire contents of the element with the range
    range.collapse(false); // collapse the range to the end point. false means collapse to end rather than the start
    selection = window.getSelection(); // get the selection object (allows you to change selection)
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    selection.removeAllRanges(); // remove any selections already made
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    selection.addRange(range); // make the range you have just created the visible selection
  }
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getBlocks = (blocks: IBlocks) => {
  const blockArray: IBlock[] = [];
  Object.keys(blocks).forEach((documentId) => {
    Object.keys(blocks[documentId]).forEach((pageNumber) => {
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      blocks[documentId][pageNumber].forEach((block: IBlock) => {
        blockArray.push(block);
      });
    });
  });
  return blockArray;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getGroupIds = (blockGroups: IGroups) => {
  const groupIds: string[] = [];
  Object.keys(blockGroups).forEach((documentId) => {
    Object.keys(blockGroups[documentId]).forEach((pageNumber) => {
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      Object.keys(blockGroups[documentId][pageNumber]).find((groupId) => {
        groupIds.push(groupId);
      });
    });
  });
  return groupIds;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getListOfGroups = (blockGroups: IGroups): IGroup[] => {
  const groupList: IGroup[] = [];
  Object.keys(blockGroups).forEach((documentId) => {
    Object.keys(blockGroups[documentId]).forEach((pageNumber) => {
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      Object.keys(blockGroups[documentId][pageNumber]).find((groupId) => {
        // @ts-expect-error -- This code was written prior to enforcing lint rules
        groupList.push(blockGroups[documentId][pageNumber][groupId]);
      });
    });
  });
  return groupList;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getInvalidCheckboxes = (blocks: IBlock[], groupIds: string[], groups: IGroup[]): IBlock[] => {
  const groupBlockIds = groups
    .map((group) => {
      return group.blockIds;
    })
    .flat();
  const invalidCheckboxes = blocks.filter((b) => {
    // if block group id points to group, validate the group points to block id
    if (b.groupId && !groupBlockIds.includes(b.blockId!)) {
      return b;
    }
    return b.groupId && !groupIds.includes(b.groupId);
  });
  return invalidCheckboxes;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getInvalidCheckboxGroups = (groups: IGroup[], blocks: IBlock[], _groupIds: string[]): IGroup[] => {
  const invalidCheckboxGroups: IGroup[] = [];
  groups.forEach((group) => {
    group.blockIds.forEach((blockId) => {
      const block = blocks.find((b) => b.blockId! === blockId);
      if (!block && !invalidCheckboxGroups.includes(group)) {
        invalidCheckboxGroups.push(group);
      } else {
        // if checkbox exists with no group id, group is bad
        const groupIdOnBlock = block?.groupId;
        if (!groupIdOnBlock && !invalidCheckboxGroups.includes(group)) {
          invalidCheckboxGroups.push(group);
        }
      }
    });
  });
  return invalidCheckboxGroups;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getBlockDocumentId = (blockId: string, blocks: IBlocks) => {
  let foundDocumentId = '';
  Object.keys(blocks).forEach((documentId) => {
    Object.keys(blocks[documentId]).forEach((pageNumber) => {
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      blocks[documentId][pageNumber].forEach((block: IBlock) => {
        if (block.blockId === blockId) {
          foundDocumentId = documentId;
        }
      });
    });
  });
  return foundDocumentId;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getBlockIndex = (blockId: string, blocks: IBlocks) => {
  let foundIndex = 0;
  Object.keys(blocks).forEach((documentId) => {
    Object.keys(blocks[documentId]).forEach((pageNumber) => {
      // @ts-expect-error -- This code was written prior to enforcing lint rules
      blocks[documentId][pageNumber].forEach((block: IBlock, index: number) => {
        if (block.blockId === blockId) {
          foundIndex = index;
        }
      });
    });
  });
  return foundIndex;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const removeInvalidBlockIdsFromGroup = (group: IGroup, blocks: IBlock[]): IGroup => {
  const updatedBlockIds = group.blockIds.filter((blockId: string) => {
    const block = blocks.find((b) => b.blockId === blockId);
    if (block && block.groupId === group.id) {
      return block.blockId;
    }
    return undefined;
  });
  return { ...group, blockIds: updatedBlockIds };
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const removeInvalidBlockIdsFromGroups = (groups: IGroup[], blocks: IBlock[]): IGroup[] => {
  return groups.map((group) => {
    return removeInvalidBlockIdsFromGroup(group, blocks);
  });
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const removeGroupIdFromBlock = (block: IBlock): IBlock => {
  const { groupId, ...updatedBlock } = block;
  return updatedBlock;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const removeGroupIdsFromBlocks = (blocks: IBlock[]): IBlock[] => {
  return blocks.map((block) => {
    return removeGroupIdFromBlock(block);
  });
};

export function cleanUpInvalidBlockValuesAndLogToFaro(
  block: IBlock,
  id: string,
  faroMessage: string,
  isTemplate?: boolean
): IBlock {
  let blockHadInvalidValues = false;
  const errors: any = {};
  const currentBlockType = BLOCK_TYPES.find((bt) => bt.key === block.blockType);

  if (block.y == null || block.y < 0 || isNaN(block.y)) {
    blockHadInvalidValues = true;
    errors.y = block.y;
    block.y = 1;
  }

  if (block.x == null || block.x < 0 || isNaN(block.x)) {
    blockHadInvalidValues = true;
    errors.x = block.x;
    block.x = 1;
  }

  if (block.height == null || isNaN(block.height) || block.height < 0) {
    blockHadInvalidValues = true;
    errors.height = block.height;
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    block.fontSize = currentBlockType?.size?.fontSize?.default;
    block.height = currentBlockType?.size?.default.height;
  }

  if (block.width == null || isNaN(block.width) || block.width < 0) {
    errors.width = block.width;
    blockHadInvalidValues = true;
    block.width = currentBlockType?.size?.default.width;
  }

  // @ts-expect-error -- This code was written prior to enforcing lint rules
  if (block.fontSize == null || block.fontSize >= block.height || isNaN(block.fontSize) || block.fontSize < 0) {
    errors.fontSize = block.fontSize;
    // @ts-expect-error -- This code was written prior to enforcing lint rules
    block.fontSize = currentBlockType?.size?.fontSize?.default;
  }

  if (blockHadInvalidValues) {
    faro?.api?.pushLog([faroMessage], {
      level: LogLevel.WARN,
      context: {
        ...(isTemplate ? { templateId: id } : { envelopeId: id }),
        block: JSON.stringify(block),
        errors: JSON.stringify(errors),
      },
    });
  }

  return block;
}

export function checkForInvalidBlockValuesAndLogToFaro(args: {
  envelopeId: string;
  x: number;
  y: number;
  msg: string;
  isXAndY: boolean;
  extraErrorProperties?: any;
}) {
  const { envelopeId, x: blockXOrWidth, y: blockYOrHeight, msg, isXAndY, extraErrorProperties } = args;

  const blockValues: any = extraErrorProperties ? { ...extraErrorProperties } : {};
  let blockHadNulls = false;
  if (blockXOrWidth < 0 || blockXOrWidth === null || Number.isNaN(blockXOrWidth)) {
    blockHadNulls = true;
  }
  if (blockYOrHeight < 0 || blockYOrHeight === null || Number.isNaN(blockYOrHeight)) {
    blockHadNulls = true;
  }
  if (blockHadNulls) {
    if (isXAndY) {
      blockValues.x = blockXOrWidth;
      blockValues.y = blockYOrHeight;
    } else {
      blockValues.width = blockXOrWidth;
      blockValues.height = blockYOrHeight;
    }

    faro?.api?.pushLog([msg], {
      level: LogLevel.WARN,
      context: {
        envelopeId,
        error: JSON.stringify(blockValues),
      },
    });
  }
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const checkIfTemplateSharedToAnyUsersGroup = (shares: IShareRecord[], usersGroups: string[]): boolean => {
  if (!shares?.length || !usersGroups?.length) return false;
  return shares.some((share) => {
    return share.shareType !== 'Group'
      ? false
      : usersGroups.find((s) => {
          return s === share.id;
        });
  });
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const checkIfUserIsInUserExclusions = (userExclusions: string[], user: IUser) => {
  if (!userExclusions.length || !user) {
    return false;
  }
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  return userExclusions.includes(user.oktaUserId);
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const checkIfTemplateIsSharedToUserInOffice = (
  user: ITemplateUser,
  officeUsers: OfficeUser[] | { oktaUserID: string }[],
  userExclusions: string[]
) => officeUsers.some((oU) => oU.oktaUserID === user.oktaUserId && !userExclusions.includes(user.oktaUserId));

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const checkIfTemplateSharedToParticularGroup = (shares: IShareRecord[], groupId: string): boolean => {
  if (!shares?.length) return false;
  return shares.some((share) => share.id === groupId);
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const checkIfTemplateSharedToAllUsersGroups = (groups: string[], shares: IShareRecord[]): boolean => {
  if (!groups?.length || !shares?.length) return false;
  const sharedGroupIds = shares?.flatMap((s) => (s.shareType === 'Group' ? s.id : []));
  // eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
  const includesAllGroups = (group: string) => sharedGroupIds?.includes(group);
  return groups?.every(includesAllGroups);
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const checkIfUserIsExcludedFromTemplateShare = (exclusions: string[], userOktaId: string) => {
  if (!exclusions?.length) return false;
  return exclusions.includes(userOktaId);
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const toggleGroupAlphabeticalSort = (groups: SubGroup[], sortByDesc: boolean) => {
  if (sortByDesc) {
    return groups.sort((b, a) => {
      if (a.name === null || a.name === '') return 1;
      if (b.name === null || b.name === '') return -1;
      return a.name.toUpperCase().localeCompare(b.name.toUpperCase(), 'de', { sensitivity: 'base' });
    });
  }
  return groups.sort((a, b) => {
    if (a.name === null || a.name === '') return 1;
    if (b.name === null || b.name === '') return -1;
    return a.name.toUpperCase().localeCompare(b.name.toUpperCase(), 'de', { sensitivity: 'base' });
  });
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const toggleOfficeAlphabeticalSort = (offices: IUserOffice[], sortByDesc: boolean) => {
  if (sortByDesc) {
    return offices.sort((b, a) => {
      if (a.officeName === null || a.officeName === '') return 1;
      if (b.officeName === null || b.officeName === '') return -1;
      return a.officeName.toUpperCase().localeCompare(b.officeName.toUpperCase(), 'de', { sensitivity: 'base' });
    });
  }

  const sortedOffices = offices.sort((a, b) => {
    if (a.officeName === null || a.officeName === '') return 1;
    if (b.officeName === null || b.officeName === '') return -1;
    return a.officeName.toUpperCase().localeCompare(b.officeName.toUpperCase(), 'de', { sensitivity: 'base' });
  });

  // moving the All office to the top of the list.
  const allOfficeIndex = sortedOffices.findIndex((office) => office.officeGuid === allOfficeGuid);
  if (allOfficeIndex > -1) {
    const allOffice = sortedOffices.splice(allOfficeIndex, 1)[0];
    sortedOffices.unshift(allOffice);
  }

  return sortedOffices;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const determineStrikeType = (yValue: number, startY: number, startX: number, mouseY: number, mouseX: number) => {
  let strikeType: StrikeType | undefined;
  if (yValue === startY) {
    strikeType = undefined;
  } else if ((mouseX > startX && mouseY > startY) || (mouseX < startX && mouseY < startY)) {
    strikeType = StrikeType.Down;
  } else if ((mouseX < startX && mouseY > startY) || (mouseX > startX && mouseY < startY)) {
    strikeType = StrikeType.Up;
  }
  return strikeType;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const buildStreetAddress = (property: IProperty): string => {
  const streetParts = [property.streetNumber, property.direction, property.streetAddress, property.unit];
  return streetParts.filter((s) => typeof s === 'string' && s.length).join(' ');
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const propertyToString = (property: IProperty) => {
  // 1234 N Foo St #123
  const streetStr = buildStreetAddress(property);
  // CA 95820
  const endParts = [property.state, property.zip];
  const endStr = endParts.filter((s) => typeof s === 'string' && s.length).join(' ');
  // Combine everything
  const fullParts = [streetStr, property.city, endStr];
  return fullParts.filter((s) => typeof s === 'string' && s.length).join(', ');
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getFileType = (metadata: IEnvelopeMetadata) => {
  return (metadata.fileType = metadata.listingGuid == null ? 'sale' : 'listing');
};

export const blockMenuResize = {
  bottom: false,
  bottomLeft: false,
  bottomRight: false,
  left: false,
  right: false,
  top: false,
  topLeft: false,
  topRight: false,
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getMultipleBlockProperties = (selectedBlocks: IBlock[]) => {
  const blockProperties: string[] = [];
  const blocksAreSignatureInitialOrText = selectedBlocks?.every(
    (block) =>
      block.blockType === BLOCK_TYPE_KEYS.SIGNATURE ||
      block.blockType === BLOCK_TYPE_KEYS.INITIALS ||
      block.blockType === BLOCK_TYPE_KEYS.TEXT
  );
  const blocksAreSingleCheckboxOrText = selectedBlocks?.every(
    (block) =>
      block.blockType === BLOCK_TYPE_KEYS.TEXT || (block.blockType === BLOCK_TYPE_KEYS.CHECKBOX && !block.groupId)
  );
  const blocksAreAllSingleCheckbox = selectedBlocks?.every(
    (block) => block.blockType === BLOCK_TYPE_KEYS.CHECKBOX && !block.groupId
  );
  const blocksAreAllTextBlocks = selectedBlocks?.every((block) => block.blockType === BLOCK_TYPE_KEYS.TEXT);
  const blocksAreAllCheckboxesInSameGroup = selectedBlocks?.every(
    (block) =>
      block.blockType === BLOCK_TYPE_KEYS.CHECKBOX && block.groupId && block.groupId === selectedBlocks[0].groupId
  );

  const someBlocksAreReadOnly = selectedBlocks?.some((block) => block.readOnly);
  const allBlocksAreReadOnly = selectedBlocks?.every((block) => block.readOnly);

  if (blocksAreSignatureInitialOrText && (!someBlocksAreReadOnly || allBlocksAreReadOnly)) {
    blockProperties.push('required');
    if (blocksAreAllTextBlocks) blockProperties.push('readOnly');
  } else if (blocksAreSingleCheckboxOrText && !blocksAreAllSingleCheckbox) {
    blockProperties.push('readOnly');
  } else if (blocksAreAllCheckboxesInSameGroup || blocksAreAllSingleCheckbox) {
    blockProperties.push('readOnly', 'checked');
  }
  return blockProperties;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getRecipientColor = (colorCounter: number, title?: string) => {
  if (colorCounter === 0 || title === 'Me') return colors.blue[800];
  return SIGNER_COLORS[(colorCounter - 1) % SIGNER_COLORS.length];
};

export function sortRecipients(recipients: IRecipient[] | ISigner[] | ITemplateRecipient[]) {
  return recipients.sort((a, b) => {
    return (
      Number(b.recipientColorCounter != null) - Number(a.recipientColorCounter != null) ||
      a.recipientColorCounter - b.recipientColorCounter
    );
  });
}

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getHighestRecipientCounter = (recipients: IRecipient[]) => {
  let highestColorCount = 0;
  recipients.forEach((recipient) => {
    if (recipient.recipientColorCounter > highestColorCount) {
      highestColorCount = recipient.recipientColorCounter;
    }
  });
  return highestColorCount;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const applyTemporaryColorCounter = (
  newRecipient: IRecipient | ISigner,
  recipients: IRecipient[],
  envelopeColorCounter?: number
) => {
  // if there is an envelope color counter we minus 1 because signer colors are zero based and envelopeColorCounter is not
  let newRecipientColorCounter = envelopeColorCounter ? envelopeColorCounter - 1 : 0;
  const highestRecipientColorCount = getHighestRecipientCounter(recipients);
  const recipientsHaveHigherCountThanEnvelope =
    envelopeColorCounter && highestRecipientColorCount >= envelopeColorCounter;

  if (newRecipient.title === 'Me') return 0;
  if (recipients?.some((rec) => rec.recipientColorCounter)) {
    newRecipientColorCounter =
      recipientsHaveHigherCountThanEnvelope || !envelopeColorCounter
        ? highestRecipientColorCount
        : envelopeColorCounter - 1;
  }
  newRecipientColorCounter += 1;
  return newRecipientColorCounter;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const applyColorAndCounterToNewRecipient = (
  newRecipients: IRecipient[],
  currentRecipients: IRecipient[] | ISigner[],
  colorCounter?: number
) => {
  const newRecipient = newRecipients[newRecipients.length - 1];
  if (newRecipients.length > currentRecipients.length && (!newRecipient.color || !newRecipient.recipientColorCounter)) {
    if (!newRecipient.recipientColorCounter) {
      newRecipient.recipientColorCounter = applyTemporaryColorCounter(newRecipient, newRecipients, colorCounter);
    }
    if ((!newRecipient.color || newRecipient.color === colors.grey[600]) && newRecipient.title != 'Me') {
      newRecipient.color = getRecipientColor(newRecipient.recipientColorCounter);
    }
  }
  return newRecipients;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const getBlocksBasedOnBlockType = (blocks: IBlock[], blockType: string): IBlock[] => {
  return blocks.filter((b) => b.blockType === blockType);
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const handleFontSizing = (
  height: number,
  width: number,
  zoom: number,
  initialFontSize: number,
  initialValue: string
) => {
  const defaultFontSize = Math.min(height - 1, initialFontSize || 24, 24);
  const value = initialValue || '';
  const newFontSize = handleCalculateFontSize(value, defaultFontSize, height, width, zoom);
  return newFontSize;
};

const hiddenDiv = document.createElement('div');
const style = {
  fontFamily: 'courier',
  overflow: 'hidden',
  boxSizing: 'content-box',
  wordBreak: 'break-word',
  textAlign: 'left',
  lineHeight: 1,
  position: 'fixed',
  whiteSpace: 'break-spaces',
  visibility: 'hidden',
};
for (const key in style) {
  // @ts-expect-error -- This code was written prior to enforcing lint rules
  hiddenDiv.style[key] = style[key];
}
document.body.appendChild(hiddenDiv);

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const shrink = (fontSize: number, height: number, zoom: number): number => {
  let newFontSize = Math.min(fontSize, height) - 1;
  while (newFontSize > 0) {
    hiddenDiv.style.fontSize = `${newFontSize * zoom}px`;
    if (hiddenDiv.clientHeight < height * zoom) {
      return newFontSize;
    }
    newFontSize--;
  }
  return 0;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
const grow = (fontSize: number, height: number, zoom: number): number => {
  const maxFontSize = Math.min(height - 1, 25);
  let newFontSize = Math.min(fontSize + 1, maxFontSize);
  while (newFontSize <= maxFontSize) {
    hiddenDiv.style.fontSize = `${newFontSize * zoom}px`;
    if (hiddenDiv.clientHeight < height * zoom) {
      newFontSize++;
    } else {
      return newFontSize - 1;
    }
  }
  return newFontSize - 1;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const handleCalculateFontSize = (
  value: string,
  fontSize: number,
  height: number,
  width: number,
  zoom: number
): number => {
  hiddenDiv.style.width = `${width * zoom - 8}px`;
  hiddenDiv.textContent = value;
  hiddenDiv.style.fontSize = `${fontSize * zoom}px`;
  if (hiddenDiv.clientHeight >= height * zoom || fontSize >= height) {
    return shrink(fontSize, height, zoom);
  }
  return grow(fontSize, height, zoom);
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const validateAndCorrectTextBlocks = (blocks: IBlock[], zoom: number) => {
  const textBlocks: IBlock[] = getBlocksBasedOnBlockType(blocks, 'Text');
  const textBlocksToUpdate: IBlock[] = [];
  textBlocks.forEach((b) => {
    if (!b.isEditingDisabled) {
      const fontSize = handleFontSizing(b.height!, b.width!, zoom, b.fontSize!, b.value as string);
      if (b.fontSize && b.fontSize > fontSize) {
        b = { ...b, fontSize };
        textBlocksToUpdate.push(b);
      }
    }
  });

  return textBlocksToUpdate;
};

// eslint-disable-next-line func-style -- This code was written prior to enforcing lint rules
export const isInactiveTemplateRecipient = (recipient: IRecipient) => {
  return (
    recipient.isPlaceholder &&
    !recipient.isActive &&
    ((!recipient.firstName && !recipient.lastName) || !recipient.email)
  );
};

type RecipientRankedMatch = null | { recipient: IRecipient; rank: number };

/**
 * Given a recipient, find the best matching recipient from a list of recipients
 * @param mappedRecipientIds - IDs of recipients that have already been mapped
 * @param recipients - List of recipients to search
 * @param recipientToMatch - The recipient to find a match for
 * @param titleMatchIndex How many recipients with this title have been matched previously
 * @returns The best matching recipient, or null if no match is found.
 */
export function getBestRecipientMatchForOptionalRecipient(
  mappedRecipientIds: string[],
  recipients: IRecipient[],
  recipientToMatch: IRecipient,
  titleMatchIndex: number
): IRecipient | null {
  // First, try to find a best match against recipients added to the envelope, including
  // template placeholder recipients that were added to the envelope, atleast at some point
  let match = recipients.reduce((result, r) => {
    if (mappedRecipientIds.includes(r.id) || isInactiveTemplateRecipient(r)) return result;

    if (
      eqIgnoreCase(r.title, recipientToMatch.title) &&
      eqIgnoreCase(r.firstName, recipientToMatch.firstName) &&
      eqIgnoreCase(r.lastName, recipientToMatch.lastName) &&
      eqIgnoreCase(r.email, recipientToMatch.email)
    ) {
      return { recipient: r, rank: 1 };
    }
    if (
      eqIgnoreCase(r.title, recipientToMatch.title) &&
      eqIgnoreCase(r.firstName, recipientToMatch.firstName) &&
      eqIgnoreCase(r.lastName, recipientToMatch.lastName)
    ) {
      if (result == null || result.rank > 2) return { recipient: r, rank: 2 };
    }
    // prettier-ignore
    if (
      eqIgnoreCase(r.title, recipientToMatch.title) &&
      eqIgnoreCase(r.email, recipientToMatch.email)
    ) {
      if (result == null || result.rank > 3) return { recipient: r, rank: 3 };
    }

    return result;
  }, null as RecipientRankedMatch);

  // if a match is found, return them
  if (match) return match.recipient;

  // if no match, try to match with template placeholder recipients
  // When matching by title only, preserve order by matching the Nth Prime recipient
  // with the Nth template recipient of that title

  match = recipients.reduce((result, r) => {
    // Skip already mapped recipients
    if (mappedRecipientIds.includes(r.id)) return result;

    // For inactive template recipients, try to match only by title
    if (!isInactiveTemplateRecipient(r) || !eqIgnoreCase(r.title, recipientToMatch.title)) return result;

    // Count recipients with this title that have a lower recipientColorCounter
    // recipientColorCounter represents the original order of the recipients (e.g. from Prime contacts)
    // instead of the current array position.
    let prevRecipientsWithThisTitle = 0;
    recipients.forEach((otherRecipient) => {
      if (
        otherRecipient.id !== r.id &&
        eqIgnoreCase(otherRecipient.title, recipientToMatch.title) &&
        otherRecipient.recipientColorCounter < r.recipientColorCounter
      ) {
        prevRecipientsWithThisTitle++;
      }
    });

    // Match when the position equals the titleMatchIndex
    if (prevRecipientsWithThisTitle === titleMatchIndex) {
      return { recipient: r, rank: 1 };
    } else {
      if (result == null || result.rank > 2) return { recipient: r, rank: 2 };
    }

    return result;
  }, null as RecipientRankedMatch);

  return match?.recipient ?? null;
}

export function handleAppLogout() {
  localStorage.removeItem('_trialBanner');
  window.location.pathname = window.location.pathname.includes('templates') ? '/templates/logout' : '/logout';
}

export async function getUserEnvelopeCounts() {
  let envelopesCreated;
  let envelopesSent;
  let envelopesCompleted;
  const res = await getEnvelopeCounts();
  envelopesCreated = res.envelopesCreated;
  envelopesSent = res.envelopesSent;
  envelopesCompleted = res.envelopesCompleted;
  return {
    Ds3EnvelopesCreated: envelopesCreated,
    Ds3EnvelopesSent: envelopesSent,
    Ds3EnvelopesCompleted: envelopesCompleted,
  };
}

/**
 * Ignoring case, are the two strings equal?
 * Nulls will always return false.
 */
function eqIgnoreCase(a: Nullable<string>, b: Nullable<string>) {
  if (a == null || b == null) return false;
  const comparison = a.localeCompare(b, undefined, { sensitivity: 'base' });
  return comparison === 0;
}
